// app/(todos)/todos/lib/forecastClient.ts
export type WeatherCondition = { text: string; icon: string };

export type WeatherHour = {
  time: string; // "YYYY-MM-DD HH:mm" (WeatherAPI) или ISO
  temp_c: number;
  condition: WeatherCondition;
};

export type ForecastDay = {
  date: string; // "YYYY-MM-DD"
  hour: WeatherHour[];
};

const FORECAST_TTL_MS = 30 * 60 * 1000;

const forecastCache: {
  data: ForecastDay[] | null;
  fetchedAt: number;
  promise: Promise<ForecastDay[]> | null;
} = { data: null, fetchedAt: 0, promise: null };

function ymd(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ✅ Надёжно достаём час из строки WeatherAPI: "YYYY-MM-DD HH:mm"
function hourFromWeatherTime(time: string) {
  if (time && time.length >= 13) {
    const hh = Number(time.slice(11, 13));
    if (Number.isFinite(hh)) return hh;
  }
  const d = new Date(time);
  const hh = d.getHours();
  return Number.isFinite(hh) ? hh : 0;
}

export async function getForecast(): Promise<ForecastDay[]> {
  const now = Date.now();

  if (forecastCache.data && now - forecastCache.fetchedAt < FORECAST_TTL_MS) {
    return forecastCache.data;
  }

  if (!forecastCache.promise) {
    forecastCache.promise = fetch('/api/todos/forecast?days=10', {
      cache: 'no-store',
      credentials: 'same-origin',
    })
      .then(async (res) => {
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Forecast request failed: ${res.status} ${t.slice(0, 200)}`);
        }
        const json = (await res.json()) as unknown;
        if (!Array.isArray(json)) throw new Error('Forecast payload is not an array');
        return json as ForecastDay[];
      })
      .then((data) => {
        forecastCache.data = data;
        forecastCache.fetchedAt = Date.now();
        return data;
      })
      .finally(() => {
        forecastCache.promise = null;
      });
  }

  return forecastCache.promise;
}

export function buildWeatherByTaskId(
  tasks: readonly { id: string; dueDate: Date | string | null }[],
  forecast: ForecastDay[] | null,
): Map<string, WeatherHour | null> {
  const map = new Map<string, WeatherHour | null>();
  if (!forecast) {
    for (const t of tasks) map.set(t.id, null);
    return map;
  }

  const byDate = new Map<string, ForecastDay>();
  for (const d of forecast) byDate.set(d.date, d);

  for (const t of tasks) {
    if (!t.dueDate) {
      map.set(t.id, null);
      continue;
    }

    const dt = new Date(t.dueDate);
    const day = byDate.get(ymd(dt));
    if (!day?.hour?.length) {
      map.set(t.id, null);
      continue;
    }

    let best: WeatherHour | null = null;
    let bestDiff = Number.POSITIVE_INFINITY;

    const target = dt.getTime();
    for (const h of day.hour) {
      const ht = new Date(h.time).getTime();
      const diff = Math.abs(ht - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = h;
      }
    }

    map.set(t.id, best);
  }

  return map;
}
